import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionState } from "@/hooks/useSessionState";
import {
  fetchReferralStats,
  fetchReferralStatsOverview,
  ReferralStats,
} from "@/api/referrals";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import {
  Search,
  Filter,
  Users,
  TrendingUp,
  Gift,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { usePagination } from "@/hooks/usePagination";

const ReferralStatsList = () => {
  const [searchTerm, setSearchTerm] = useSessionState("referralStatsList.searchTerm", "");
  const [currentPage, setCurrentPage] = useSessionState("referralStatsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("referralStatsList.pageSize", 10);

  // Fetch referral stats overview (for cards)
  const { data: statsOverview } = useQuery({
    queryKey: ["referralStatsOverview"],
    queryFn: fetchReferralStatsOverview,
  });

  // Fetch all referral stats (client-side pagination)
  const { data: statsData, isLoading } = useQuery({
    queryKey: ["referralStats"],
    queryFn: () => fetchReferralStats({}), // Fetch all data without pagination
  });

  const filteredStats =
    statsData?.data.filter(
      (stat) =>
        stat.referrer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stat.referred_user_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    ) || [];

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Calculate pagination (client-side)
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredStats.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  // Get current page items
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentStats = filteredStats.slice(startIndex, endIndex);

  // Use overview stats for cards (independent of pagination)
  const totalStats = statsOverview?.totalReferrals || 0;
  const rewardedStats = statsOverview?.rewardedReferrals || 0;
  const totalRewardAmount = statsOverview?.totalRewardAmount || 0;
  const uniqueReferrers = statsOverview?.uniqueReferrers || 0;

  return (
    <AdminLayout title="Referral Stats">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Referrals
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Rewarded Referrals
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rewardedStats}</div>
            <p className="text-xs text-muted-foreground">With rewards</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Referrers
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueReferrers}</div>
            <p className="text-xs text-muted-foreground">Unique users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rewards</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ₹{totalRewardAmount.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">Allocated amount</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by referrer or referred user name..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredStats.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">
              No referral stats found
            </p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search criteria
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Referred User</TableHead>
                  <TableHead>Subscription ID</TableHead>
                  <TableHead>Reward Cycle</TableHead>
                  <TableHead>Reward Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentStats.map((stat) => (
                  <TableRow key={stat.id}>
                    <TableCell className="font-medium">
                      {stat.referrer_name || `User ${stat.referrer_id}`}
                    </TableCell>
                    <TableCell>
                      {stat.referred_user_name ||
                        `User ${stat.referred_user_id}`}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      #{stat.subscription_id}
                    </TableCell>
                    <TableCell>
                      {stat.reward_cycle > 0 ? (
                        <Badge variant="outline">
                          Cycle {stat.reward_cycle}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-gray-50 text-gray-600"
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      {stat.reward_amount > 0
                        ? `₹${Number(stat.reward_amount).toFixed(2)}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {stat.reward_granted ? (
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Rewarded
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-700 border-yellow-200"
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(stat.created_at), "MMM d, yyyy")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(stat.created_at), "h:mm a")}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredStats.length > 0 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Showing{" "}
              <span className="font-medium">
                {filteredStats.length === 0
                  ? 0
                  : (currentPage - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, filteredStats.length)}
              </span>{" "}
              of <span className="font-medium">{filteredStats.length}</span>{" "}
              referral stats
            </div>
            <div className="flex items-center gap-4">
              {totalPages > 0 && (
                <div className="flex items-center">
                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={!canGoPrev}
                  >
                    First
                  </Button>

                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                    disabled={!canGoPrev}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {pageNumbers.map((pageNum) => (
                    <Button
                      key={pageNum}
                      className="mx-0.5 w-8 h-8 p-0"
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ))}

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(
                        currentPage < totalPages ? currentPage + 1 : totalPages
                      )
                    }
                    disabled={!canGoNext}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={!canGoNext}
                  >
                    Last
                  </Button>
                </div>
              )}

              <select
                className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
              >
                {[10, 20, 30, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Card>
    </AdminLayout>
  );
};

export default ReferralStatsList;
